import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkeletonCard } from '@/components/ui/SkeletonLoader';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { cursoService } from '@/services/api';
import { Curso, PaginationParams, PaginationResponse } from '@/types';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';

export const CursosPage: React.FC = () => {
  const [filters, setFilters] = useState<PaginationParams>({
    page: 1,
    limit: 10,
  });
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateCursos } = useQueryInvalidation();
  const { dialogState, showConfirm, closeDialog } = useConfirmDialog();

  const [searchText, setSearchText] = useState<string>("");

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1, search: searchText || undefined }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchText]);

  const [loadError, setLoadError] = useState<string | null>(null);

  const { data: cursosData, isLoading, error } = useQuery<PaginationResponse<Curso>>({
    queryKey: ['cursos', filters],
    queryFn: () => cursoService.list(filters),
  });

  useEffect(() => {
    if (error) {
      const message = handleError(error as any, 'Erro ao carregar cursos');
      setLoadError(message);
    }
  }, [error, handleError]);

  useEffect(() => {
    if (cursosData) {
      setLoadError(null);
    }
  }, [cursosData]);

  const deleteCursoMutation = useMutation({
    mutationFn: cursoService.delete,
    onSuccess: (_data, deletedId) => {
      invalidateCursos(deletedId);
      handleSuccess('Curso excluído com sucesso!');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao excluir curso');
    },
  });

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleDelete = async (id: number) => {
    showConfirm(
      'Excluir Curso',
      'Tem certeza que deseja excluir este curso? Esta ação não pode ser desfeita.',
      async () => {
        await deleteCursoMutation.mutateAsync(id);
      },
      'danger'
    );
  };

  if (loadError) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cursos</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie os cursos cadastrados no sistema
          </p>
        </div>
        <Link to="/cursos/novo">
          <Button>
            <PlusIcon className="h-4 w-4 mr-2" />
            Novo Curso
          </Button>
        </Link>
      </div>

      {/* Cursos List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Lista de Cursos</CardTitle>
              <CardDescription>
                {cursosData?.pagination.total || 0} cursos encontrados
              </CardDescription>
            </div>
            <div className="w-full md:w-72 relative">
              <input
                type="text"
                placeholder="Buscar por nome, habilitação ou nível"
                className="w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText && (
                <button
                  type="button"
                  aria-label="Limpar busca"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchText("")}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonCard count={5} />
          ) : cursosData?.data.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Nenhum curso encontrado</p>
              <Link to="/cursos/novo">
                <Button className="mt-4">Cadastrar primeiro curso</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {cursosData?.data.map((curso: Curso) => (
                <div
                  key={curso.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">{curso.nome}</h3>
                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {curso.habilitacao && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Habilitação:</span> {curso.habilitacao}
                          </p>
                        )}
                        {curso.nivel && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Nível:</span> {curso.nivel}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Link to={`/cursos/${curso.id}/editar`}>
                        <Button variant="outline" size="sm" className="text-yellow-600 hover:text-yellow-700 hover:border-yellow-300" title="Editar">
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(curso.id)}
                        disabled={deleteCursoMutation.isPending}
                        className="text-red-600 hover:text-red-700 hover:border-red-300"
                        title="Excluir"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {cursosData && cursosData.pagination.totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! - 1)}
                  disabled={!cursosData.pagination.hasPrev}
                >
                  Anterior
                </Button>
                <span className="flex items-center px-3 text-sm text-gray-700">
                  Página {cursosData.pagination.page} de {cursosData.pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! + 1)}
                  disabled={!cursosData.pagination.hasNext}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText="Excluir"
        cancelText="Cancelar"
        isLoading={deleteCursoMutation.isPending}
      />
    </div>
  );
};
